/**
 * src/shell/agents/services/CanvasTelemetryConnector.ts
 *
 * Real-Time Telemetry Stream Connector for Generative Canvas Widgets.
 * Provides live simulated and WebSockets feeds for:
 * 1. `telemetry:nodes` — Connected mesh peers, ping latencies, and CPU loads.
 * 2. `access:doors` — Real-time BLE proximity unlock assertions and alerts.
 * 3. `ledger:credits` — microCredit transfer velocity and distribution.
 * 4. `mesh:bandwidth` — Gossip protocol throughput and packet counts.
 */

import { EndpointType } from '../types/genui-types';

export type TelemetryPayload = Record<string, unknown>;
export type TelemetrySubscriber = (data: TelemetryPayload) => void;

export class CanvasTelemetryConnector {
  private static instance: CanvasTelemetryConnector;
  private subscribers: Map<EndpointType, Set<TelemetrySubscriber>> = new Map();
  private intervalIds: Map<EndpointType, number> = new Map();

  private constructor() {}

  public static getInstance(): CanvasTelemetryConnector {
    if (!CanvasTelemetryConnector.instance) {
      CanvasTelemetryConnector.instance = new CanvasTelemetryConnector();
    }
    return CanvasTelemetryConnector.instance;
  }

  public subscribe(
    endpoint: EndpointType,
    intervalMs: number = 2000,
    callback: TelemetrySubscriber
  ): () => void {
    if (!this.subscribers.has(endpoint)) {
      this.subscribers.set(endpoint, new Set());
    }

    this.subscribers.get(endpoint)!.add(callback);

    // Immediately trigger first tick
    callback(this.generateMockData(endpoint));

    // Start interval if not running
    if (!this.intervalIds.has(endpoint)) {
      const id = window.setInterval(() => {
        const payload = this.generateMockData(endpoint);
        const subs = this.subscribers.get(endpoint);
        if (subs) {
          subs.forEach((cb) => {
            try {
              cb(payload);
            } catch {}
          });
        }
      }, Math.max(500, intervalMs));

      this.intervalIds.set(endpoint, id);
    }

    return () => {
      const subs = this.subscribers.get(endpoint);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          const timerId = this.intervalIds.get(endpoint);
          if (timerId) {
            clearInterval(timerId);
            this.intervalIds.delete(endpoint);
          }
        }
      }
    };
  }

  public generateMockData(endpoint: EndpointType): TelemetryPayload {
    const timestamp = new Date().toISOString();

    switch (endpoint) {
      case 'telemetry:nodes':
        return {
          timestamp,
          activeNodes: 8,
          avgLatencyMs: Math.floor(28 + Math.random() * 14),
          nodes: [
            { id: 'node_1', label: 'Edge-Alpha-01', latency: 24, status: 'online' },
            { id: 'node_2', label: 'Edge-Alpha-02', latency: 31, status: 'online' },
            { id: 'node_3', label: 'Relay-Beta-09', latency: 45, status: 'online' },
            { id: 'node_4', label: 'Core-Sentinel', latency: 12, status: 'online' },
          ],
        };

      case 'access:doors':
        return {
          timestamp,
          doorEvents: [
            {
              id: `evt_${Date.now()}_1`,
              door: 'Server Room 4B',
              user: 'admin_security',
              authMethod: 'BLE_PROXIMITY',
              status: 'GRANTED',
              time: 'Just now',
            },
            {
              id: `evt_${Date.now()}_2`,
              door: 'Main Entrance Gate',
              user: 'operator_dan',
              authMethod: 'NFC_BADGE',
              status: 'GRANTED',
              time: '2m ago',
            },
            {
              id: `evt_${Date.now()}_3`,
              door: 'Vault Room Alpha',
              user: 'guest_unauth',
              authMethod: 'KEYPAD',
              status: 'DENIED',
              time: '5m ago',
            },
          ],
          hourlyDensity: [
            { hour: '08:00', density: 14 },
            { hour: '10:00', density: 42 },
            { hour: '12:00', density: 78 },
            { hour: '14:00', density: 55 },
            { hour: '16:00', density: 34 },
            { hour: '18:00', density: 19 },
          ],
        };

      case 'ledger:credits':
        return {
          timestamp,
          balance: 1420.5,
          velocityPerMin: 14.8,
          allocations: [
            { source: 'Compute Yield', target: 'Node-1 Relay', amount: 450 },
            { source: 'Compute Yield', target: 'Storage Node 3', amount: 320 },
            { source: 'Bandwidth Pool', target: 'Peer Gateway', amount: 650 },
          ],
        };

      case 'mesh:bandwidth':
      default:
        return {
          timestamp,
          incomingMbps: 42.6,
          outgoingMbps: 38.2,
          packetLossPct: 0.02,
        };
    }
  }
}

export const canvasTelemetryConnector = CanvasTelemetryConnector.getInstance();
